import { useState, useEffect } from 'react';
import { getProjects, createProject, updateProject, deleteProject, getClients, getUsers, getCustomFields, getContracts, getProjectContracts, getBulkProjectContracts, getProducts, getBusinessUnits, deleteCompensationPlan } from '../lib/api';
import { useAuth } from '../lib/auth';
import { differenceInDays, parseISO } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Combobox } from '../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Pencil, Trash2, FolderKanban, Search, Loader2, Users, Euro, FileText, AlertTriangle, Package, Building2, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ProjectAssignments from '../components/ProjectAssignments';
import ProjectContracts from '../components/ProjectContracts';
import EntityChangeLog from '../components/EntityChangeLog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { PROJECT_FUNCTIONS, assignUserToProject } from '../lib/api';
import { MultiSelect } from '../components/ui/multi-select';

const commissionTypes = [
  { value: 'percentage', label: 'Percentuale (%)' },
  { value: 'fixed', label: 'Fisso (€)' },
  { value: 'custom', label: 'Personalizzato' },
];

export default function ProjectsPage() {
  const { hasRole } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [projectContractsMap, setProjectContractsMap] = useState({}); // Mappa project_id -> contratti associati
  const [venditori, setVenditori] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allBUs, setAllBUs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Filtri
  const [filterClients, setFilterClients] = useState([]);
  const [filterAccounts, setFilterAccounts] = useState([]);
  const [filterMembers, setFilterMembers] = useState([]); // PM + Venditori + Supervisor
  const [filterBUs, setFilterBUs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkUserId, setBulkUserId] = useState('');
  const [bulkSystemRole, setBulkSystemRole] = useState('pm');
  const [bulkProjectFunction, setBulkProjectFunction] = useState('venditore');
  const [bulkSaving, setBulkSaving] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    commission_type: 'percentage',
    commission_value: '',
    commission_description: '',
    assigned_venditori: [],
    assigned_project_managers: [],
    assigned_account_id: '',
    custom_fields: {},
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsRes, clientsRes, contractsRes, venditoriRes, pmsRes, accountsRes, fieldsRes, productsRes, busRes] = await Promise.all([
        getProjects(),
        hasRole(['admin', 'hr', 'project_manager']) ? getClients() : Promise.resolve({ data: [] }),
        hasRole(['admin', 'hr', 'project_manager']) ? getContracts() : Promise.resolve({ data: [] }),
        hasRole(['admin', 'hr', 'project_manager']) ? getUsers('venditore') : Promise.resolve({ data: [] }),
        hasRole(['admin', 'hr', 'project_manager']) ? getUsers('project_manager') : Promise.resolve({ data: [] }),
        hasRole(['admin', 'hr', 'project_manager']) ? getUsers('account') : Promise.resolve({ data: [] }),
        getCustomFields('project'),
        getProducts().catch(() => ({ data: [] })),
        getBusinessUnits().catch(() => ({ data: [] })),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setContracts(contractsRes.data);
      setVenditori(venditoriRes.data);
      setProjectManagers(pmsRes.data);
      setAccounts(accountsRes.data);
      setCustomFields(fieldsRes.data);
      setAllProducts(productsRes.data);
      setAllBUs(busRes.data);
      
      // Carica i contratti per tutti i progetti in una singola chiamata bulk
      const projectIds = projectsRes.data.map(p => p.id);
      let contractsMap = {};
      if (projectIds.length > 0) {
        try {
          const bulkRes = await getBulkProjectContracts(projectIds, true);
          contractsMap = bulkRes.data;
        } catch {
          projectIds.forEach(pid => { contractsMap[pid] = []; });
        }
      }
      setProjectContractsMap(contractsMap);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare i dati', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };


  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name,
        description: project.description || '',
        client_id: project.client_id,
        commission_type: project.commission?.commission_type || 'percentage',
        commission_value: project.commission?.value?.toString() || '',
        commission_description: project.commission?.description || '',
        assigned_venditori: project.assigned_venditori || [],
        assigned_project_managers: project.assigned_project_managers || [],
        assigned_account_id: project.assigned_account_id || '',
        custom_fields: project.custom_fields || {},
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        description: '',
        client_id: '',
        commission_type: 'percentage',
        commission_value: '',
        commission_description: '',
        assigned_venditori: [],
        assigned_project_managers: [],
        assigned_account_id: '',
        custom_fields: {},
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Cliente obbligatorio in creazione e modifica
    if (!formData.client_id) {
      toast({ title: 'Cliente obbligatorio', description: 'Seleziona un cliente per il progetto.', variant: 'destructive' });
      return;
    }
    
    // Validate required custom fields
    const missingCF = customFields.filter(f => f.is_required && !formData.custom_fields[f.id]);
    if (missingCF.length > 0) {
      toast({ title: 'Campi obbligatori', description: `Compila: ${missingCF.map(f => f.name).join(', ')}`, variant: 'destructive' });
      return;
    }

    // For existing projects with contracts, check products are assigned
    if (editingProject) {
      const pcs = projectContractsMap[editingProject.id] || [];
      const activePCs = pcs.filter(pc => !pc.end_date && pc.is_active);
      if (activePCs.length > 0) {
        const hasProducts = activePCs.some(pc => (pc.selected_product_ids || []).length > 0);
        if (!hasProducts) {
          toast({ title: 'Prodotti obbligatori', description: 'Seleziona almeno un prodotto per ogni contratto associato.', variant: 'destructive' });
          return;
        }
      }
    }
    
    setSaving(true);
    try {
      const data = {
        name: formData.name,
        description: formData.description || null,
        client_id: formData.client_id,
        commission: {
          commission_type: formData.commission_type,
          value: parseFloat(formData.commission_value) || 0,
          description: formData.commission_description || null,
        },
        assigned_venditori: formData.assigned_venditori,
        assigned_project_managers: formData.assigned_project_managers,
        assigned_account_id: formData.assigned_account_id || null,
        custom_fields: formData.custom_fields,
      };

      if (editingProject) {
        await updateProject(editingProject.id, data);
        toast({ title: 'Progetto aggiornato' });
      } else {
        await createProject(data);
        toast({ title: 'Progetto creato' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Eliminare il progetto "${project.name}"?`)) return;
    try {
      await deleteProject(project.id);
      toast({ title: 'Progetto eliminato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile eliminare il progetto', variant: 'destructive' });
    }
  };

  const toggleVenditore = (vendId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_venditori: prev.assigned_venditori.includes(vendId)
        ? prev.assigned_venditori.filter((id) => id !== vendId)
        : [...prev.assigned_venditori, vendId],
    }));
  };

  const toggleProjectManager = (pmId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_project_managers: prev.assigned_project_managers.includes(pmId)
        ? prev.assigned_project_managers.filter((id) => id !== pmId)
        : [...prev.assigned_project_managers, pmId],
    }));
  };

  const getClientName = (clientId) => {
    return clients.find((c) => c.id === clientId)?.company_name || 'N/A';
  };

  const getContract = (contractId) => {
    return contracts.find((c) => c.id === contractId);
  };

  const isContractExpired = (contractId) => {
    const contract = getContract(contractId);
    if (!contract || !contract.end_date) return false;
    const endDate = typeof contract.end_date === 'string' ? parseISO(contract.end_date) : contract.end_date;
    return differenceInDays(endDate, new Date()) < 0;
  };

  const getContractsForClient = (clientId) => {
    return contracts.filter((c) => c.client_id === clientId);
  };

  // Helper per ottenere info sui contratti di un progetto
  const getProjectContractsInfo = (projectId) => {
    const projectContracts = projectContractsMap[projectId] || [];
    const activeContracts = projectContracts.filter(pc => pc.is_active);
    
    let expiredCount = 0;
    activeContracts.forEach(pc => {
      const contract = contracts.find(c => c.id === pc.contract_id);
      if (contract && contract.end_date) {
        const endDate = typeof contract.end_date === 'string' ? parseISO(contract.end_date) : contract.end_date;
        if (differenceInDays(endDate, new Date()) < 0) {
          expiredCount++;
        }
      }
    });
    
    return {
      total: activeContracts.length,
      expired: expiredCount
    };
  };

  const filteredProjects = projects.filter((p) => {
    // Search term
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Client filter
    if (filterClients.length > 0 && !filterClients.includes(p.client_id)) return false;
    
    // Account Manager filter (via client.assigned_account_id)
    if (filterAccounts.length > 0) {
      const client = clients.find(c => c.id === p.client_id);
      if (!client || !filterAccounts.includes(client.assigned_account_id)) return false;
    }
    
    // Members filter (PM + Venditori + Supervisor)
    if (filterMembers.length > 0) {
      const allMembers = [
        ...(p.assigned_pms || []),
        ...(p.assigned_supervisors || []),
        ...(p.assigned_venditori || []),
      ];
      const hasAny = filterMembers.some(uid => allMembers.includes(uid));
      if (!hasAny) return false;
    }
    
    // Business Unit filter
    if (filterBUs.length > 0) {
      const buids = p.business_unit_ids || [];
      const hasAny = filterBUs.some(b => buids.includes(b));
      if (!hasAny) return false;
    }
    
    return true;
  });
  
  const hasActiveFilters = searchTerm || filterClients.length > 0 || filterAccounts.length > 0 || filterMembers.length > 0 || filterBUs.length > 0;
  
  const resetFilters = () => {
    setSearchTerm('');
    setFilterClients([]);
    setFilterAccounts([]);
    setFilterMembers([]);
    setFilterBUs([]);
  };
  
  // Members deduped (PM + Supervisor + Venditori + Account)
  const membersOptions = (() => {
    const map = new Map();
    [...projectManagers, ...venditori, ...accounts].forEach(u => { if (u && !map.has(u.id)) map.set(u.id, u); });
    return Array.from(map.values());
  })();

  // Bulk selection helpers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProjects.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProjects.map(p => p.id)));
    }
  };
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkAssign = async () => {
    if (!bulkUserId || selectedIds.size === 0) {
      toast({ title: 'Seleziona un utente', variant: 'destructive' });
      return;
    }
    setBulkSaving(true);
    let ok = 0;
    let fail = 0;
    for (const pid of selectedIds) {
      try {
        await assignUserToProject({
          project_id: pid,
          user_id: bulkUserId,
          system_role: bulkSystemRole,
          project_function: bulkProjectFunction,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkSaving(false);
    toast({ title: 'Assegnazione completata', description: `${ok} progetti aggiornati${fail ? `, ${fail} errori` : ''}` });
    setBulkAssignOpen(false);
    setBulkUserId('');
    setBulkSystemRole('pm');
    setBulkProjectFunction('venditore');
    setSelectedIds(new Set());
    loadData();
  };

  const handleBulkDeletePlans = async () => {
    if (!window.confirm(`Eliminare tutti i piani compensi fissi di ${selectedIds.size} progetti?\nLe rate verranno eliminate anche da Compensi. Le rate approvate/saldate verranno mantenute.`)) return;
    setBulkSaving(true);
    try {
      const res = await deleteCompensationPlan({ project_ids: Array.from(selectedIds) });
      toast({ title: res.data.message });
      setSelectedIds(new Set());
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    } finally {
      setBulkSaving(false);
    }
  };

  // Build a flat list of selectable users for bulk dialog (deduped by id)
  const bulkUsersOptions = (() => {
    const map = new Map();
    [
      ...projectManagers.map(u => ({ id: u.id, name: u.name, group: 'Project Manager' })),
      ...venditori.map(u => ({ id: u.id, name: u.name, group: 'Operativi' })),
      ...accounts.map(u => ({ id: u.id, name: u.name, group: 'Account' })),
    ].forEach(o => { if (!map.has(o.id)) map.set(o.id, o); });
    return Array.from(map.values());
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Progetti</h1>
          <p className="text-gray-500">Gestisci i progetti e i Success Fee</p>
        </div>
        {hasRole(['admin', 'hr', 'project_manager']) && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Progetto
          </Button>
        )}
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="py-3 px-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca progetti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                data-testid="search-projects"
              />
            </div>
            <div className="w-[200px]">
              <MultiSelect
                options={clients.map(c => ({ value: c.id, label: c.company_name }))}
                selected={filterClients}
                onChange={setFilterClients}
                placeholder="Cliente"
              />
            </div>
            <div className="w-[200px]">
              <MultiSelect
                options={accounts.filter(a => a.is_active !== false).map(a => ({ value: a.id, label: a.name }))}
                selected={filterAccounts}
                onChange={setFilterAccounts}
                placeholder="Account Manager"
              />
            </div>
            <div className="w-[220px]">
              <MultiSelect
                options={membersOptions.map(u => ({ value: u.id, label: u.name }))}
                selected={filterMembers}
                onChange={setFilterMembers}
                placeholder="Figure assegnate"
              />
            </div>
            <div className="w-[200px]">
              <MultiSelect
                options={allBUs.map(b => ({ value: b.id, label: b.name }))}
                selected={filterBUs}
                onChange={setFilterBUs}
                placeholder="Business Unit"
              />
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500" onClick={resetFilters} data-testid="projects-reset-filters">
                Reset filtri
              </Button>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {filteredProjects.length} progett{filteredProjects.length === 1 ? 'o' : 'i'} {hasActiveFilters ? 'filtrat' + (filteredProjects.length === 1 ? 'o' : 'i') : 'totali'}
          </div>
        </CardContent>
      </Card>

      {filteredProjects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderKanban className="w-12 h-12 mx-auto text-gray-300" />
            <p className="mt-4 text-gray-500">Nessun progetto trovato</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table data-testid="projects-table">
              <TableHeader>
                <TableRow>
                  {hasRole(['admin', 'hr', 'project_manager']) && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedIds.size > 0 && selectedIds.size === filteredProjects.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-projects"
                      />
                    </TableHead>
                  )}
                  <TableHead>Progetto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Contratti</TableHead>
                  <TableHead>Business Unit</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((project) => {
                  const contractsInfo = getProjectContractsInfo(project.id);
                  const pids = project.product_ids || [];
                  const buids = project.business_unit_ids || [];
                  const teamSize = (project.assigned_venditori?.length || 0) + (project.assigned_pms?.length || 0) + (project.assigned_supervisors?.length || 0);
                  return (
                    <TableRow key={project.id} data-testid={`project-row-${project.id}`} className={selectedIds.has(project.id) ? 'bg-orange-50/40' : ''}>
                      {hasRole(['admin', 'hr', 'project_manager']) && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(project.id)}
                            onCheckedChange={() => toggleSelect(project.id)}
                            data-testid={`select-project-${project.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => handleOpenDialog(project)}
                          className="text-left font-medium text-gray-900 hover:text-orange-600"
                        >
                          {project.name}
                        </button>
                        {project.description && (
                          <div className="text-xs text-gray-500 line-clamp-1">{project.description}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{getClientName(project.client_id)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                          <Users className="w-3.5 h-3.5 text-orange-500" />
                          {teamSize}
                        </span>
                      </TableCell>
                      <TableCell>
                        {contractsInfo.total === 0 ? (
                          <span className="text-xs text-gray-400">Nessuno</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-sm">
                            <FileText className={`w-3.5 h-3.5 ${contractsInfo.expired > 0 ? 'text-red-500' : 'text-blue-600'}`} />
                            {contractsInfo.total}
                            {contractsInfo.expired > 0 && (
                              <Badge variant="destructive" className="text-[10px] py-0 px-1 flex items-center gap-0.5">
                                <AlertTriangle className="w-2.5 h-2.5" />{contractsInfo.expired} scad.
                              </Badge>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {buids.slice(0, 3).map(buId => {
                            const bu = allBUs.find(b => b.id === buId);
                            return bu ? (
                              <Badge key={buId} variant="outline" className="text-[10px] py-0">
                                <Building2 className="w-2.5 h-2.5 mr-0.5" />{bu.name}
                              </Badge>
                            ) : null;
                          })}
                          {buids.length > 3 && <span className="text-[10px] text-gray-400">+{buids.length - 3}</span>}
                          {contractsInfo.total > 0 && pids.length === 0 && (
                            <Badge variant="outline" className="text-[10px] py-0 text-amber-600 border-amber-300">
                              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Prodotti?
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={project.is_active ? 'default' : 'secondary'} className="text-[10px]">
                          {project.is_active ? 'Attivo' : 'Inattivo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {hasRole(['admin', 'hr', 'project_manager', 'account']) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenDialog(project)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                {hasRole(['admin', 'hr', 'project_manager']) ? 'Modifica' : 'Dettagli'}
                              </DropdownMenuItem>
                              {hasRole(['admin', 'project_manager']) && (
                                <DropdownMenuItem onClick={() => handleDelete(project)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Bulk action bar */}
      {hasRole(['admin', 'hr', 'project_manager']) && selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white border shadow-xl rounded-full px-5 py-2 flex items-center gap-3" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-gray-700">
            {selectedIds.size} progett{selectedIds.size === 1 ? 'o' : 'i'} selezionat{selectedIds.size === 1 ? 'o' : 'i'}
          </span>
          <Button
            size="sm"
            onClick={() => setBulkAssignOpen(true)}
            data-testid="bulk-assign-btn"
          >
            <Users className="w-3.5 h-3.5 mr-1.5" />
            Assegna risorsa
          </Button>
          {hasRole(['admin']) && (
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleBulkDeletePlans} disabled={bulkSaving} data-testid="bulk-delete-plans-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Elimina piani compensi
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-selection">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assegna risorsa ai progetti selezionati</DialogTitle>
            <DialogDescription>
              La stessa persona verrà aggiunta come membro su tutti i {selectedIds.size} progetti selezionati.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Persona</Label>
              <Select value={bulkUserId} onValueChange={setBulkUserId}>
                <SelectTrigger data-testid="bulk-user-select">
                  <SelectValue placeholder="Seleziona persona" />
                </SelectTrigger>
                <SelectContent>
                  {bulkUsersOptions.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} <span className="text-gray-400 text-xs">({u.group})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Ruolo di sistema</Label>
                <Select value={bulkSystemRole} onValueChange={setBulkSystemRole}>
                  <SelectTrigger data-testid="bulk-system-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pm">Project Manager</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="sales">Operativo / Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Funzione progetto</Label>
                <Select value={bulkProjectFunction} onValueChange={setBulkProjectFunction}>
                  <SelectTrigger data-testid="bulk-project-function-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_FUNCTIONS.map(fn => (
                      <SelectItem key={fn.value} value={fn.value}>{fn.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignOpen(false)}>Annulla</Button>
            <Button onClick={handleBulkAssign} disabled={bulkSaving} data-testid="confirm-bulk-assign-btn">
              {bulkSaving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Assegna a {selectedIds.size} progett{selectedIds.size === 1 ? 'o' : 'i'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject 
                ? (hasRole(['admin', 'hr', 'project_manager']) ? 'Modifica Progetto' : 'Dettagli Progetto')
                : 'Nuovo Progetto'
              }
            </DialogTitle>
            <DialogDescription>
              {editingProject 
                ? (hasRole(['admin', 'hr', 'project_manager']) ? 'Modifica i dati del progetto' : 'Visualizza i dettagli del progetto')
                : 'Inserisci i dati del nuovo progetto'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Progetto *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="project-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client">Cliente *</Label>
                <Combobox
                  options={clients.map(c => ({ value: c.id, label: c.company_name }))}
                  value={formData.client_id}
                  onChange={(v) => setFormData({ ...formData, client_id: v })}
                  placeholder="Seleziona cliente"
                  searchPlaceholder="Cerca cliente..."
                  emptyText="Nessun cliente trovato"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium">Success Fee</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={formData.commission_type}
                    onValueChange={(v) => setFormData({ ...formData, commission_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {commissionTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valore</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.commission_value}
                    onChange={(e) => setFormData({ ...formData, commission_value: e.target.value })}
                    placeholder={formData.commission_type === 'percentage' ? 'es. 10' : 'es. 100'}
                    data-testid="project-commission-value"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note Success Fee</Label>
                <Input
                  value={formData.commission_description}
                  onChange={(e) => setFormData({ ...formData, commission_description: e.target.value })}
                  placeholder="es. Bonus extra per target raggiunti"
                />
              </div>
            </div>

            {/* Team Progetto - Nuovo sistema con storico */}
            {editingProject ? (
              <div className="border rounded-lg p-4">
                <ProjectAssignments 
                  projectId={editingProject.id} 
                  canEdit={hasRole('admin') || hasRole('hr') || hasRole('project_manager')} 
                />
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
                <Users className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p>Salva prima il progetto per assegnare il team</p>
              </div>
            )}

            {/* Contratti Associati - Nuovo sistema multi-contratto */}
            {editingProject ? (
              <div className="border rounded-lg p-4">
                <ProjectContracts 
                  projectId={editingProject.id}
                  clientId={editingProject.client_id || formData.client_id}
                  canEdit={hasRole('admin') || hasRole('project_manager') || hasRole('account')} 
                />
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
                <Euro className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p>Salva prima il progetto per associare i contratti</p>
              </div>
            )}

            {/* Change Log - only show when editing */}
            {editingProject && (
              <div className="border rounded-lg p-4">
                <EntityChangeLog entityType="project" entityId={editingProject.id} />
              </div>
            )}

            {customFields.length > 0 && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium">Campi Personalizzati</h4>
                {customFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label>{field.name} {field.is_required && '*'}</Label>
                    {field.field_type === 'text' && (
                      <Input
                        value={formData.custom_fields[field.id] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: e.target.value },
                          })
                        }
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'number' && (
                      <Input
                        type="number"
                        value={formData.custom_fields[field.id] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: e.target.value },
                          })
                        }
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'select' && (
                      <Select
                        value={formData.custom_fields[field.id] || ''}
                        onValueChange={(v) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: v === '__none__' ? '' : v },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          {!field.is_required && (
                            <SelectItem value="__none__">
                              <span className="text-gray-400">Nessuno</span>
                            </SelectItem>
                          )}
                          {field.options?.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {field.field_type === 'boolean' && (
                      <Checkbox
                        checked={formData.custom_fields[field.id] || false}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: checked },
                          })
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {hasRole(['admin', 'hr', 'project_manager']) ? 'Annulla' : 'Chiudi'}
              </Button>
              {hasRole(['admin', 'hr', 'project_manager']) && (
                <Button type="submit" disabled={saving} data-testid="save-project-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingProject ? 'Salva Modifiche' : 'Crea Progetto'}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
